import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Search, Loader2, FileText, BookOpen, MessageSquare, Utensils,
  Heart, Clock, Filter, Sparkles, RefreshCw
} from "lucide-react";
import { pageTransition, fadeUp } from "@/lib/animations";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPE_ICONS = {
  vault: FileText,
  journal: BookOpen,
  prompt: MessageSquare,
  meal: Utensils,
  legacy: Heart,
};

const TYPE_LABELS = {
  vault: "Vault",
  journal: "Journal",
  prompt: "Prompt",
  meal: "Meal",
  legacy: "Legacy",
};

const TYPE_COLORS = {
  vault: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  journal: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  prompt: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  meal: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  legacy: "bg-pink-500/10 text-pink-400 border-pink-500/30",
};

function SearchResult({ result }) {
  const Icon = TYPE_ICONS[result.content_type] || FileText;
  const content = result.content || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 hover:border-white/10 transition-all"
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${TYPE_COLORS[result.content_type]?.split(' ')[0] || 'bg-white/5'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className={`text-xs ${TYPE_COLORS[result.content_type]}`}>
              {TYPE_LABELS[result.content_type]}
            </Badge>
            {result.similarity && (
              <span className="text-xs text-slate-500">
                {Math.round(result.similarity * 100)}% match
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-white mb-1">
            {content.title || content.subject || content.prompt_text || "Untitled"}
          </p>
          <p className="text-xs text-slate-400 line-clamp-2">
            {result.text_preview || content.content || content.description || ""}
          </p>
          {result.metadata?.created_at && (
            <div className="flex items-center gap-1 mt-2 text-xs text-slate-600">
              <Clock className="w-3 h-3" />
              {new Date(result.metadata.created_at).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function SearchPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchType, setSearchType] = useState("semantic");
  const [indexStats, setIndexStats] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);

  useEffect(() => {
    loadIndexStats();
  }, []);

  const loadIndexStats = async () => {
    try {
      const res = await api.get("/search/index-stats");
      setIndexStats(res.data);
    } catch { /* ignore */ }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const typesParam = selectedTypes.length > 0 ? `&types=${selectedTypes.join(",")}` : "";
      const endpoint = searchType === "semantic" 
        ? `/search/semantic?q=${encodeURIComponent(query)}${typesParam}&limit=30`
        : `/search?q=${encodeURIComponent(query)}`;
      
      const res = await api.get(endpoint);
      setResults(res.data.results || []);
      
      if (res.data.results?.length === 0) {
        toast.info("No results found. Try different keywords or reindex your content.");
      }
    } catch {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReindex = async () => {
    setReindexing(true);
    try {
      await api.post("/search/reindex");
      toast.success("Reindexing started! This may take a moment...");
      // Poll for completion
      setTimeout(loadIndexStats, 3000);
    } catch {
      toast.error("Failed to start reindexing");
    } finally {
      setReindexing(false);
    }
  };

  const toggleType = (type) => {
    setSelectedTypes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-10 max-w-[900px] mx-auto pb-24" data-testid="search-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#00FF88]" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] font-medium">AI Search</p>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>
          Memory Search
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Search through all your memories using natural language
        </p>
      </div>

      {/* Search Box */}
      <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-4 mb-6">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search your memories... (e.g., 'happy moments with family')"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              data-testid="search-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12 pl-11"
            />
          </div>
          <Button
            onClick={handleSearch}
            disabled={loading || !query.trim()}
            data-testid="search-btn"
            className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-xl px-6 h-12"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </Button>
        </div>

        {/* Filter Types */}
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-xs text-slate-500 mr-2">Filter:</span>
          {["vault", "journal", "prompt", "meal", "legacy"].map((type) => {
            const Icon = TYPE_ICONS[type];
            const isSelected = selectedTypes.includes(type);
            return (
              <button
                key={type}
                onClick={() => toggleType(type)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  isSelected
                    ? TYPE_COLORS[type]
                    : "bg-white/5 text-slate-500 hover:bg-white/10"
                }`}
              >
                <Icon className="w-3 h-3" />
                {TYPE_LABELS[type]}
              </button>
            );
          })}
        </div>

        {/* Search Type Toggle */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/[0.06]">
          <button
            onClick={() => setSearchType("semantic")}
            className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
              searchType === "semantic" ? "text-[#00FF88]" : "text-slate-500 hover:text-white"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" /> Smart Search
          </button>
          <button
            onClick={() => setSearchType("keyword")}
            className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
              searchType === "keyword" ? "text-[#00FF88]" : "text-slate-500 hover:text-white"
            }`}
          >
            <Search className="w-3.5 h-3.5" /> Keyword Match
          </button>
        </div>
      </div>

      {/* Index Stats */}
      {indexStats && (
        <div className="flex items-center justify-between mb-6 bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-sm text-white font-medium">{indexStats.total_indexed} items indexed</p>
              <p className="text-xs text-slate-500">
                {Object.entries(indexStats.by_type || {}).map(([k, v]) => `${v} ${k}`).join(" • ") || "No content indexed"}
              </p>
            </div>
          </div>
          <Button
            onClick={handleReindex}
            disabled={reindexing}
            variant="outline"
            size="sm"
            className="border-white/10 text-slate-400 hover:text-white rounded-full"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${reindexing ? "animate-spin" : ""}`} />
            {reindexing ? "Indexing..." : "Reindex"}
          </Button>
        </div>
      )}

      {/* Results */}
      {results.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-500 mb-4">{results.length} results found</p>
          {results.map((result, i) => (
            <SearchResult key={`${result.content_type}-${result.content_id || i}`} result={result} />
          ))}
        </div>
      ) : query && !loading ? (
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-12 text-center">
          <Search className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Manrope" }}>
            No results
          </h3>
          <p className="text-sm text-slate-400 max-w-sm mx-auto">
            Try different keywords or click "Reindex" to update your search index.
          </p>
        </div>
      ) : (
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-12 text-center">
          <Sparkles className="w-10 h-10 text-[#00FF88] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Manrope" }}>
            Smart Memory Search
          </h3>
          <p className="text-sm text-slate-400 max-w-sm mx-auto">
            Search naturally across your vault, journal, prompts, meals, and legacy messages.
          </p>
        </div>
      )}
    </motion.div>
  );
}
